import { EntityLevel } from '@/composables/useEntityLevel'
import type { CoreType } from '@/interfaces/core.interface'
import { LocaleString, QuickSelectType } from '../components/types'
import { FormatOptions } from '../components/AnalyticsDashboard/formatter'
import { Feature } from '@/pinia/feature'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { ColorScaleConfig, createColorScale } from '@/utils/colorScale'
import {
  Aggregation,
  AggregationQuery
} from '@/components/AnalyticsDashboard/measurements/contracts'
import { darkenColor, lightenColor } from '@/utils/rgba'
import {
  DateSelection,
  DateSelectionType,
  dateSelectionTypes
} from '@/interfaces/dateselection.interface'
import { dateSelections } from '@/features/dateSelections'
import { DeepPartial, DeepPartialNull } from '@/utils/types'
import { BarConfiguration } from '@/components/AnalyticsDashboard/components/toplist-types'
import _ from 'lodash'
import { ResourceType } from '@/components/AnalyticsDashboard/measurements/useMeasurements'
import { Ticks } from '@/components/AnalyticsDashboard/components/ChartComponent/chart/ticks'

const customMerge = (
  destVal: unknown | undefined | null,
  srcVal: unknown | undefined | null
): unknown | undefined => {
  if (srcVal === null) {
    return undefined
  }
  if (Array.isArray(srcVal) && srcVal.length === 0) {
    return []
  }
  return Array.isArray(destVal) ? destVal.concat(srcVal) : undefined
}

export function merge<T>(
  obj1: DeepPartialNull<T> | undefined,
  ...others: (DeepPartialNull<T> | undefined)[]
): DeepPartial<T> {
  return _.mergeWith(obj1 ?? {}, ...others, customMerge)
}

export type Dashboard = {
  name: string
  title: LocaleString
  dataSectionTitle?: LocaleString
  info?: LocaleString
  link?: {
    address: string
    name: LocaleString
  }
  enabled: boolean
  icon: string
  tabs?: Tab[]
  defaultToggle?: string
  // Opt-in: with nothing or a region selected, group the map coloring at building depth instead of by
  // complex, so each value colors the building, or the real estate area when it isn't linked to a
  // building. A complex is already grouped at building depth. Any analysis whose data is linked to
  // real estates colors their areas, opted in or not.
  coloredEntitiesAtBuildingDepth?: boolean
  // Notes on the map popups saying which level a colored value is linked to
  mapLevelNotes?: {
    building?: LocaleString
    realEstate?: LocaleString
  }
  measurements: Measurement[]
  components: DashboardComponent[]
  enabledEntityLevels?: EntityLevel[]
  defaultFilters: {
    calendar: {
      from?: string
      to?: string
      quickSelect?: QuickSelectType
    }
    defaultDateSelection: DateSelectionType
    dateSelections?: DateSelectionType[]
    bucketSize?: BucketSize
    onlyBusinessHours?: boolean
    onlyBusinessDays?: boolean
    onlyBusinessHoursFilterType?: 'query' | 'suffix'
    tags?: Record<string, string[]>
    filterTags?: string[]
    entityLevels?: CoreType[]
  }
}

export type Tab = {
  name: string
  title: LocaleString
  components: string[]
  initialShowOnMapComponent?: string // Name of the component that should show on map initially
}

export type LegendType = 'gradient' | 'discrete' | 'slider'

type SliderLegend = {
  fromColor?: string
  toColor?: string
  middleColor?: string
}

export type Legend<T extends LegendType = LegendType> = T extends 'slider'
  ? SliderLegend & { type: 'slider' }
  : never

export type Measurement = {
  name: string
  field: Aggregation
  title: LocaleString
  filters?: ComponentFilters
  options: {
    color?: string
    colorScale?: ColorScaleConfig
    legend?: Legend
    format: FormatOptions
    enabledEntityLevels?: EntityLevel[]
    min?: number
    max?: number
  }
}

export enum DashboardComponentType {
  Kpi = 'kpi',
  TimeSeries = 'timeseries',
  Histogram = 'histogram',
  Heatmap = 'heatmap',
  Toplist = 'toplist',
  BarChart = 'barchart'
}

export type DashboardComponent<T = DashboardComponentType> = T extends DashboardComponentType.Kpi
  ? KpiComponent & SharedComponent<DashboardComponentType.Kpi>
  : T extends DashboardComponentType.TimeSeries
    ? TimeSeriesComponent & SharedComponent<DashboardComponentType.TimeSeries>
    : T extends DashboardComponentType.Histogram
      ? HistogramComponent & SharedComponent<DashboardComponentType.Histogram>
      : T extends DashboardComponentType.Toplist
        ? ToplistComponent & SharedComponent<DashboardComponentType.Toplist>
        : T extends DashboardComponentType.BarChart
          ? BarChartComponent & SharedComponent<DashboardComponentType.BarChart>
          : T extends DashboardComponentType.Heatmap
            ? HeatmapComponent & SharedComponent<DashboardComponentType.Heatmap>
            : never

export type ComponentFilters<T extends DashboardComponentType = DashboardComponentType> = Partial<
  Record<ResourceType<T>, DeepPartialNull<MeasurementFilters>>
>

export type SharedComponent<T extends DashboardComponentType = DashboardComponentType> = {
  type: T
  name: string
  enabled?: boolean
  title?: LocaleString
  info?: LocaleString
  filters?: ComponentFilters<T>
  measurementOverride?: DeepPartialNull<Measurement>
}

export type KpiComponent = {
  measurement?: string
  measurements?: string[]
  meta?: Record<string, Metadata>
  details?: {
    groups: string[]
    fields?: string[]
  }
  disableMap?: boolean
}

export type HistogramComponent = {
  measurements: string[]
  normalize?: boolean
  showRanges?: boolean
}

export type TimeSeriesComponent = {
  measurements: string[]
  yAxisTicks?: Ticks
}

export type ToplistComponent = {
  measurements: string[]
  grouping?: 'stacked' | 'grouped'
}

export type BarChartComponent = {
  measurements: string[]
  groupKey?: string
  subGroupKey?: string
  subBarFormats?: Record<string, { label: LocaleString; color: string }>
  barFormats?: Record<string, { label: LocaleString; color: string }>
  grouping?: 'stacked' | 'grouped'
  codomainTicks?: Ticks
}

export type HeatmapComponent = {
  measurement: string
}

// export type GroupByType = 'entity' | 'time' | 'time-dow' | 'time-hour'

export type MeasurementFilters = {
  query: AggregationQuery
  suffixFilters?: string[]
}

export type Metadata = {
  field: string
}

export type MeasurementSource<T extends DashboardComponentType = DashboardComponentType> =
  T extends DashboardComponentType.Kpi
    ? {
        type: DashboardComponentType.Kpi
        filters?: Partial<Record<ResourceType<T>, DeepPartialNull<MeasurementFilters>>>
        componentName: string
        measurement: Measurement
        field: Aggregation
        meta?: Record<string, Metadata>
      }
    : {
        type: T
        filters?: Partial<Record<ResourceType<T>, DeepPartialNull<MeasurementFilters>>>
        componentName: string
        measurement: Measurement
        field: Aggregation
      }

type MeasurementMap = Record<string, Measurement | undefined>

const defaultColorScale: ColorScaleConfig = {
  type: 'INTERPOLATE',
  cutoffs: [
    {
      color: '#E6F7FF',
      value: 0
    },
    {
      color: '#40A9FF',
      value: 50
    },
    {
      color: '#003A8C',
      value: 100
    }
  ]
}

export function compileDashboardFeature(dashboard: Dashboard): Feature {
  const componentNameSet = new Set(dashboard.components.map((c) => c.name))
  const hasUniqueComponentNames = componentNameSet.size === dashboard.components.length
  if (!hasUniqueComponentNames) {
    const duplicateComponentNames = dashboard.components
      .map((c) => c.name)
      .filter((name, index, self) => self.indexOf(name) !== index)
    throw new Error(`Duplicate component names: ${duplicateComponentNames.join(', ')}`)
  }
  const role = `FEAT_${dashboard.name.toUpperCase()}_READER`
  const measurementMap: MeasurementMap = dashboard.measurements.reduce((acc, measurement) => {
    acc[`${measurement.name}.${measurement.field.name}`] = measurement
    return acc
  }, {} as MeasurementMap)

  // If no tabs, create a default tab with all components
  const effectiveTabs =
    dashboard.tabs && dashboard.tabs.length > 0
      ? dashboard.tabs
      : [
          {
            name: 'default',
            title: dashboard.dataSectionTitle ?? dashboard.title,
            components: dashboard.components.map((c) => c.name),
            // Default the first component to show on map if it's not specified
            initialShowOnMapComponent:
              dashboard.components.length > 0 ? dashboard.components[0].name : undefined
          }
        ]

  // First we'll compile measurements for all components
  let allMeasurementSources: MeasurementSource[] = []

  // For each tab, compile the components with correct initialShowOnMap settings
  const compiledTabsChildren = effectiveTabs.map((tab) => {
    // Find which component should show on map initially (if any)
    const showOnMapComponent = tab.initialShowOnMapComponent

    // Compile each component for this tab
    const tabComponentFeatures = tab.components.map((componentName) => {
      const component = dashboard.components.find((c) => c.name === componentName)
      if (!component) {
        throw new Error(`Component ${componentName} referenced in tab ${tab.name} not found`)
      }

      // Should this component initially show on map?
      const shouldShowOnMap = showOnMapComponent === componentName

      const [feature, measurementSources] = compileComponent(
        component,
        // Apply component measurement override for all measurements in map using lodash _.merge
        Object.entries(measurementMap).reduce((acc, [key, measurement]) => {
          const componentMeasurement = component.measurementOverride
          if (componentMeasurement) {
            const mergedMeasurement = merge<Measurement>(
              {},
              measurement,
              componentMeasurement
            ) as Measurement
            acc[key] = mergedMeasurement
          } else {
            acc[key] = measurement
          }
          return acc
        }, {} as MeasurementMap),
        shouldShowOnMap
      )

      // Collect all measurement sources
      allMeasurementSources = [...allMeasurementSources, ...measurementSources]

      return feature
    })

    const childrenWithDividers = tabComponentFeatures.flatMap((child, i): Feature[] => {
      const prevChild = tabComponentFeatures[i - 1]
      const isKpiAndOtherType =
        prevChild &&
        ((prevChild.type === DashboardComponentType.Kpi &&
          child.type !== DashboardComponentType.Kpi) ||
          (prevChild.type !== DashboardComponentType.Kpi &&
            child.type === DashboardComponentType.Kpi))

      return isKpiAndOtherType
        ? [
            {
              name: `divider-${tab.name}-${i}`,
              title: {},
              enabled: true,
              component: 'DividerComponent',
              props: {
                width: 'auto',
                fullscreenWidth: '70%'
              }
            },
            child
          ]
        : [child]
    })

    return createSection(role, dashboard, allMeasurementSources, childrenWithDividers, tab)
  })

  return {
    name: dashboard.name,
    enabled: dashboard.enabled,
    component: 'DashboardFeatureNew',
    access: [role],
    title: dashboard.title,
    isRoute: true,
    props: {
      icon: dashboard.icon,
      showCalendar: true,
      defaultDateSelection: dashboard.defaultFilters.defaultDateSelection
    },
    children: [
      {
        name: 'filters',
        enabled: true,
        title: {
          en: 'Filters',
          sv: 'Filter'
        },
        component: 'AggregateAnalysisFilters'
      },
      {
        name: 'time',
        enabled: true,
        title: {
          en: 'Time',
          sv: 'Tid'
        },
        children: (
          (dashboard.defaultFilters.dateSelections ?? dateSelectionTypes).map((name) =>
            dateSelections.find((d) => d.name === name)
          ) as DateSelection[]
        ).map((selection) => ({
          name: selection.name,
          enabled: true,
          access: selection.access,
          title: selection.title,
          props: {
            range: selection.range,
            icon: selection.icon
          }
        }))
      },
      {
        name: 'data',
        enabled: true,
        title: {
          en: 'Data',
          sv: 'Data'
        },
        children: compiledTabsChildren
      }
    ]
  }
}

function createSection(
  role: string,
  dashboard: Dashboard,
  measurementSources: MeasurementSource[],
  children: Feature[],
  tab: Tab
): Feature {
  return {
    name: tab.name,
    enabled: true,
    isRoute: true,
    access: [role],
    title: tab.title,
    info: dashboard.info,
    component: 'DashboardDataSectionDynamic',
    props: {
      enabledEntityLevels: dashboard.enabledEntityLevels ?? [
        'REGION',
        'COMPLEX',
        'REAL_ESTATE',
        'CONSTRUCTION',
        'FLOOR',
        'LEVEL',
        'SPACE'
      ],
      link: {
        address: dashboard.link?.address,
        name: dashboard.link?.name
      },
      defaultFilters: dashboard.defaultFilters,
      measurementSources: measurementSources,
      defaultToggle: dashboard.defaultToggle,
      coloredEntitiesAtBuildingDepth: dashboard.coloredEntitiesAtBuildingDepth ?? false,
      mapLevelNotes: dashboard.mapLevelNotes
    },
    children: children
  }
}

function compileComponent(
  component: DashboardComponent,
  measurementMap: MeasurementMap,
  shouldShowOnMap: boolean
): [Feature, MeasurementSource[]] {
  const [specificProps, measurements] = createSpecificComponentProps(
    component,
    measurementMap,
    shouldShowOnMap
  )
  return [
    {
      name: component.name,
      enabled: component.enabled ?? true,
      type: component.type,
      ...specificProps
    },
    measurements
  ]
}

function createSpecificComponentProps(
  component: DashboardComponent,
  measurementMap: MeasurementMap,
  shouldShowOnMap: boolean
): [Partial<Feature> & { title: LocaleString }, MeasurementSource[]] {
  switch (component.type) {
    case DashboardComponentType.Heatmap: {
      const measurement = measurementMap[component.measurement]
      if (!measurement) {
        throw new Error(
          `Measurement ${component.measurement} not found for Heatmap ${component.name}`
        )
      }
      const title = measurement.title ?? component.title ?? { en: 'Heatmap', sv: 'Värmekarta' } // Default title
      const colorScale = measurement.options.colorScale ?? defaultColorScale
      if (!colorScale) {
        console.warn(
          `Heatmap component ${component.name} measurement ${measurement.name} is missing a colorScale.`
        )
      }

      return [
        {
          component: 'CardHeatmapNew',
          title,
          info: component.info,
          props: {
            measurement,
            componentName: component.name,
            format: measurement.options.format,
            colorScale: colorScale
          }
        },
        [
          {
            measurement: measurement,
            componentName: component.name,
            field: measurement.field,
            filters: component.filters,
            type: DashboardComponentType.Heatmap
          }
        ]
      ]
    }
    case DashboardComponentType.Kpi: {
      const measurements = (
        component.measurements ?? (component.measurement ? [component.measurement] : [])
      )
        .map((measurement) => measurementMap[measurement])
        .filter((m): m is Measurement => m !== undefined)
      if (!measurements.length) {
        throw new Error(`No measurements found for KPI component ${component.name}`)
      }
      const measurement = measurements[0]!
      const title = component.title ?? measurement.title
      const color =
        measurement.options.color ?? (measurement.options.colorScale ? undefined : '#40A9FF')
      const colorScale =
        measurement.options.colorScale ??
        (color
          ? createColorScale(color, measurement.options.min ?? 0, measurement.options.max ?? 100)
          : undefined)
      const legend = generateLegendComponent(
        title,
        measurement.options.format,
        color,
        colorScale,
        measurement.options.legend
      )
      return [
        {
          component: 'MainMeasurementCardNew',
          title,
          info: component.info,
          props: {
            measurement: measurement,
            initialShowOnMap: shouldShowOnMap,
            value: {
              format: measurement.options.format
            },
            coloredEntities: component.disableMap
              ? undefined
              : { enabledEntityLevels: measurement.options.enabledEntityLevels },
            absoluteColorScale: component.disableMap ? false : undefined,
            table: component.details
              ? {
                  ...component.details
                }
              : undefined,
            colorScale,
            color: color,
            format: measurement.options.format
          },
          children: [...(legend ? [legend] : [])]
        },
        measurements.map((measurement) => ({
          measurement: measurement,
          componentName: component.name,
          field: measurement.field,
          filters: component.filters,
          meta: component.meta,
          type: DashboardComponentType.Kpi
        }))
      ]
    }
    case DashboardComponentType.TimeSeries:
    case DashboardComponentType.Histogram:
    case DashboardComponentType.BarChart: {
      const measurements = component.measurements
        .map((measurement) => measurementMap[measurement])
        .filter((m) => m) as Measurement[]
      const firstMeasurement = measurements[0]
      const firstMeasurementOptions = measurements[0].options
      return [
        {
          component:
            component.type === DashboardComponentType.TimeSeries
              ? 'TimeSeriesCard'
              : component.type === DashboardComponentType.BarChart
                ? 'BarChartCard'
                : component.type === DashboardComponentType.Histogram
                  ? 'HistogramCard'
                  : 'UnknownComponent',
          title: component.title ?? {
            en: 'Chart',
            sv: 'Diagram'
          },
          info: component.info,
          props: {
            measurements,
            component,
            colorScale: firstMeasurement?.options.colorScale,
            enableFullscreen: true,
            dataFormat: firstMeasurementOptions.format
          }
        },
        measurements.map((measurement) => ({
          measurement: measurement,
          componentName: component.name,
          filters: merge({}, measurement.filters, component.filters),
          field: measurement.field,
          type: component.type
        }))
      ]
    }
    case DashboardComponentType.Toplist: {
      const toplistMeasurements = component.measurements
        .map((measurement) => measurementMap[measurement])
        .filter((m) => m) as Measurement[]
      return [
        {
          component: 'CardTopListNew',
          title: component.title ?? {
            en: 'Toplist',
            sv: 'Topplista'
          },
          info: component.info,
          props: {
            measurements: toplistMeasurements,
            barOptions: [
              ...toplistMeasurements.map((m): BarConfiguration => ({
                label: m.title,
                color: m.options.color ?? '#0BA5EC',
                gradient: true,
                tooltip: true
              })),
              {
                label: {
                  en: 'Not measured',
                  sv: 'Ej mätt'
                },
                color: '#E0E0E0',
                gradient: true,
                tooltip: false
              }
            ],
            grouping: (component as ToplistComponent).grouping ?? 'stacked',
            format: toplistMeasurements[0].options.format
          }
        },
        toplistMeasurements.map((measurement) => ({
          measurement: measurement,
          componentName: component.name,
          field: measurement.field,
          type: component.type,
          filters: component.filters
        }))
      ]
    }
  }
}

function generateLegendComponent(
  title: LocaleString,
  format: FormatOptions,
  color?: string,
  colorScale?: ColorScaleConfig,
  legend?: Legend
): Feature | undefined {
  if (!legend) {
    return undefined
  }
  if (!legend || !color) {
    throw new Error('Legend must be defined when color is not provided')
  }
  switch (legend.type) {
    case 'slider':
      if (!legend.fromColor || !legend.toColor || !legend.middleColor) {
        return generateDefaultLegendComponent(title, color, format)
      }
      return {
        name: 'legend',
        enabled: true,
        component: 'InteractiveColorLegend',
        title,
        props: {
          fromColor: legend.fromColor,
          toColor: legend.toColor,
          middleColor: legend.middleColor,
          format
        }
      }
  }
  throw new Error(`Unsupported legend type: ${legend.type}`)
}

function generateDefaultLegendComponent(
  title: LocaleString,
  color: string,
  format: FormatOptions
): Feature {
  return {
    name: 'legend',
    enabled: true,
    component: 'InteractiveColorLegend',
    title,
    props: {
      fromColor: lightenColor(color, 0.25),
      toColor: darkenColor(color, 0.25),
      middleColor: color,
      format
    }
  }
}
